import { TYPESENSE_PUBLISHED_PRODUCTS_FILTER } from './constants';

/** Escape a value for Typesense `field:=`backtick quoting. */
export function escapeTypesenseFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
}

/**
 * True when a single Typesense filter clause has a non-empty value after `:=`.
 * Clauses without `:=` (ranges, expressions) are kept.
 */
export function clauseHasNonEmptyValue(clause: string): boolean {
  const trimmed = clause.trim();
  if (!trimmed) return false;

  const eqIndex = trimmed.indexOf(':=');
  if (eqIndex === -1) return true;

  const raw = trimmed.slice(eqIndex + 2).trim();
  if (!raw) return false;

  if (raw.startsWith('`') && raw.endsWith('`') && raw.length >= 2) {
    return raw.slice(1, -1).trim().length > 0;
  }

  return raw.length > 0;
}

/**
 * Drop empty `field:=` / `field:=`` clauses from a raw filter string.
 * Returns undefined when nothing usable remains.
 */
export function sanitizeFilterClauses(
  filters?: string | null,
): string | undefined {
  if (!filters?.trim()) return undefined;

  const parts = filters
    .split('&&')
    .map((part) => part.trim())
    .filter(clauseHasNonEmptyValue);

  return parts.length > 0 ? parts.join(' && ') : undefined;
}

/** `field:=`value`` or undefined when value is blank. */
export function equalityFilter(
  field: string,
  value?: string | null,
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return `${field}:=\`${escapeTypesenseFilterValue(trimmed)}\``;
}

function resolveManufacturerName(
  manufacturer?: string | { name?: string } | null,
): string | undefined {
  if (manufacturer == null) return undefined;
  if (typeof manufacturer === 'string') {
    const trimmed = manufacturer.trim();
    return trimmed || undefined;
  }
  const name = manufacturer.name?.trim();
  return name || undefined;
}

/**
 * Related-products InstantSearch filter: prefer brand, else manufacturer.
 * Returns undefined when both are empty (caller should omit `filters`).
 */
export function buildRelatedProductsFilter(opts: {
  brand?: string | null;
  manufacturer?: string | { name?: string } | null;
}): string | undefined {
  const brandClause = equalityFilter('brand', opts.brand);
  if (brandClause) return brandClause;
  return equalityFilter('manufacturer', resolveManufacturerName(opts.manufacturer));
}

/**
 * Merge base published filter with an optional extra clause string.
 * Empty / invalid extras are omitted (never `brand:=`).
 */
export function appendFilterClause(
  base: string = TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
  extra?: string | null,
): string {
  const sanitized = sanitizeFilterClauses(extra);
  if (!sanitized) return base;
  return `${base} && ${sanitized}`;
}
